/* eslint-disable no-unused-vars */
import { useTranslate } from 'hooks/useTranslate';
import { FC } from 'react';

import { CoOwner, Range, SelectorOption } from 'types';
import PageWithTabs from 'modules/_shared/components/Pages/PageWithTabs';
import { icons } from 'constants/icons';
import Button from 'modules/_shared/components/Buttons';
import { FixedShare } from '../types/Share';
import { EncumbranceTypes } from '../constants/encumbranceOptions';

import ShareEncumbranceViewCoOwner from './ShareEncumbranceViewCoOwners';
import ShareEncumbranceViewFormReadOnlyData from './ShareEncumbranceViewFormReadOnlyData';
import ShareEncumbranceViewFormScriptureData from './ShareEncumbranceViewFormScriptureData';
import ShareEncumbranceViewFormEncumbranceData from './ShareEncumbranceViewFormEncumbranceData';
import ShareEncumbranceViewFormNotarialData from './ShareEncumbranceViewFormNotarialData';

type Props = {
  share: FixedShare;
  encumbranceName: EncumbranceTypes;
  encumbranceNameOption: SelectorOption | null;
  encumbranceDate: string;
  encumbranceFrom: number;
  encumbranceTo: number;
  affectedShares: number;
  encumbranceScriptureData: any;
  encumbranceFile: File | undefined;
  mainCoOwner: CoOwner;
  coOwners: CoOwner[];
  validRanges: Range[];
  inputLabel: string;
  isValidRange: boolean;
  isEditing: boolean;
  handleChangeEncumbranceName: (event: any) => void;
  handleChangeEncumbranceDate: (event: any) => void;
  handleChangeEncumbranceFrom: (event: any) => void;
  handleChangeEncumbranceTo: (event: any) => void;
  handleAddCoOwner: (coOwner: CoOwner) => void;
  handleRemoveCoOwner: (index: number) => void;
  handleAddEncumbrance: () => void;
  handleCancelEdit: () => void;
  handleClickRange: (range: Range) => void;
  handleChangeNotaryName: (event: any) => void;
  handleChangeProtocolNr: (event: any) => void;
  handleChangeNotaryCity: (event: any) => void;
  handleChangeEncumbranceFile: (file: File) => void;
};

const ShareEncumbranceViewForm: FC<Props> = ({
  share,
  encumbranceName,
  encumbranceNameOption,
  encumbranceDate,
  encumbranceFrom,
  encumbranceTo,
  affectedShares,
  encumbranceScriptureData,
  encumbranceFile,
  mainCoOwner,
  coOwners,
  validRanges,
  inputLabel,
  isValidRange,
  isEditing,
  handleChangeEncumbranceName,
  handleChangeEncumbranceDate,
  handleChangeEncumbranceFrom,
  handleChangeEncumbranceTo,
  handleAddCoOwner,
  handleRemoveCoOwner,
  handleAddEncumbrance,
  handleCancelEdit,
  handleClickRange,
  handleChangeNotaryName,
  handleChangeProtocolNr,
  handleChangeNotaryCity,
  handleChangeEncumbranceFile,
}) => {
  const { t } = useTranslate();

  const isShareRangeValid =
    encumbranceFrom >= (share?.from || 0) && encumbranceTo <= (share?.to || 0);

  const mustHaveCoOwners =
    encumbranceName !== EncumbranceTypes.SHAREHOLDER_AGREEMENTS;
  const isCoOwnerValid = coOwners?.length > 0;

  const isDisabledAddButton =
    !encumbranceName ||
    !encumbranceDate ||
    !isShareRangeValid ||
    (mustHaveCoOwners && !isCoOwnerValid);

  return (
    <>
      <div className="row g-4 pb-4">
        <ShareEncumbranceViewFormEncumbranceData
          share={share}
          encumbranceName={encumbranceName}
          encumbranceNameOption={encumbranceNameOption}
          encumbranceDate={encumbranceDate}
          encumbranceFrom={encumbranceFrom}
          encumbranceTo={encumbranceTo}
          handleChangeEncumbranceName={handleChangeEncumbranceName}
          handleChangeEncumbranceDate={handleChangeEncumbranceDate}
          handleChangeEncumbranceFrom={handleChangeEncumbranceFrom}
          handleChangeEncumbranceTo={handleChangeEncumbranceTo}
          isValidRange={isValidRange}
          validRanges={validRanges}
          handleClickRange={handleClickRange}
        />

        <ShareEncumbranceViewFormReadOnlyData
          share={share}
          affectedShares={affectedShares}
        />
      </div>

      <div className="mb-4">
        <ShareEncumbranceViewFormScriptureData
          encumbranceFile={encumbranceFile}
          inputLabel={inputLabel}
          handleChangeEncumbranceFile={handleChangeEncumbranceFile}
        />
      </div>

      <PageWithTabs
        id="share-encumbrance-documentation-tabs"
        tabs={[
          {
            key: 'co-owners-tab',
            icon: icons.CO_OWNERS,
            title: t('EncumbranceOwners'),
            content: (
              <ShareEncumbranceViewCoOwner
                mainCoOwner={mainCoOwner}
                coOwners={coOwners}
                encumbranceName={encumbranceName}
                handleAddCoOwner={handleAddCoOwner}
                handleRemoveCoOwner={handleRemoveCoOwner}
              />
            ),
          },
          {
            key: 'notarial-tab',
            icon: icons.NOTARIAL_REGISTRATION,
            title: t('NotarialRegistration'),
            content: (
              <ShareEncumbranceViewFormNotarialData
                encumbranceScriptureData={encumbranceScriptureData}
                handleChangeNotaryName={handleChangeNotaryName}
                handleChangeProtocolNr={handleChangeProtocolNr}
                handleChangeNotaryCity={handleChangeNotaryCity}
              />
            ),
          },
        ]}
      />

      <div className="form-group d-flex justify-content-end mt-4">
        <div>
          <Button
            icon="plus"
            onClick={handleAddEncumbrance}
            disabled={isDisabledAddButton}
            outlineStyle={isDisabledAddButton}
            dimStyle={isDisabledAddButton}
          >
            {isEditing ? t('UpdateEncumbrance') : t('AddEncumbrance')}
          </Button>
        </div>

        {isEditing && (
          <div className="ml-2">
            <Button
              icon="x"
              variant="danger"
              onClick={handleCancelEdit}
              outlineStyle
              dimStyle
            >
              {t('Cancel')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default ShareEncumbranceViewForm;
